import { useState, useMemo, useCallback } from 'react';
import {
  TextField,
  Select,
  Autocomplete,
  Tag,
  HorizontalStack,
  ButtonGroup,
  Button,
  Text,
  Box,
  VerticalStack,
} from '@shopify/polaris';
import { PHONE_DIAL_OPTIONS, splitPhone, composePhone } from '../../../helpers/phonePrefixes';

// ---------------------------------------------------------------------------
// Campo de teléfono con prefijo internacional (bandera + código).
// Réplica de la UX de Storelink (PhoneField.tsx). Guarda E.164: "+<prefijo><dig>".
// `value` es el valor compuesto guardado; emite el valor compuesto por onChange.
// ---------------------------------------------------------------------------
export const LeadPhoneField = ({ label = 'Teléfono', value, onChange }) => {
  const initial = splitPhone(value);
  const [dial, setDial] = useState(initial.dial);
  const [national, setNational] = useState(initial.national);

  const handleDial = useCallback(
    (d) => {
      setDial(d);
      onChange(composePhone(d, national));
    },
    [national, onChange],
  );

  const handleNational = useCallback(
    (n) => {
      const digits = n.replace(/\D/g, '');
      setNational(digits);
      onChange(composePhone(dial, digits));
    },
    [dial, onChange],
  );

  return (
    <TextField
      label={label}
      type="tel"
      inputMode="tel"
      value={national}
      onChange={handleNational}
      autoComplete="off"
      placeholder="Número"
      connectedLeft={
        <Select
          label="Prefijo internacional"
          labelHidden
          options={PHONE_DIAL_OPTIONS}
          value={dial}
          onChange={handleDial}
        />
      }
    />
  );
};

// ---------------------------------------------------------------------------
// Multiselect "bonito" con búsqueda (Polaris Autocomplete allowMultiple).
// Muestra los seleccionados como Tags dentro del campo (verticalContent).
//   options: [{ value, label, hint? }]
//   selected: array de values
// ---------------------------------------------------------------------------
export const LeadMultiAutocomplete = ({
  label,
  placeholder = 'Buscar…',
  options,
  selected,
  onChange,
  loading = false,
  helpText,
  emptyMessage = 'Sin coincidencias',
}) => {
  const [inputValue, setInputValue] = useState('');

  // Opciones para Polaris: el `label` lleva el hint (género) si lo hay.
  const polarisOptions = useMemo(
    () =>
      (options || []).map((o) => ({
        value: o.value,
        label: o.hint ? `${o.label} · ${o.hint}` : o.label,
      })),
    [options],
  );

  const filteredOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return polarisOptions;
    return polarisOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [polarisOptions, inputValue]);

  // Mapa value -> texto del Tag. Incluye el hint (género) para que dos opciones
  // con el mismo nombre pero distinto género no se confundan al verlas seleccionadas.
  const labelByValue = useMemo(() => {
    const map = {};
    (options || []).forEach((o) => {
      map[o.value] = o.hint ? `${o.label} · ${o.hint}` : o.label;
    });
    return map;
  }, [options]);

  const handleSelect = useCallback(
    (sel) => {
      onChange(sel);
      setInputValue('');
    },
    [onChange],
  );

  const removeTag = useCallback(
    (value) => () => onChange(selected.filter((v) => v !== value)),
    [selected, onChange],
  );

  const verticalContent =
    selected.length > 0 ? (
      <HorizontalStack gap="1">
        {selected.map((value) => (
          <Tag key={value} onRemove={removeTag(value)}>
            {labelByValue[value] || value}
          </Tag>
        ))}
      </HorizontalStack>
    ) : null;

  const textField = (
    <Autocomplete.TextField
      label={label}
      value={inputValue}
      onChange={setInputValue}
      placeholder={placeholder}
      autoComplete="off"
      helpText={helpText}
      verticalContent={verticalContent}
    />
  );

  return (
    <Autocomplete
      allowMultiple
      options={filteredOptions}
      selected={selected}
      onSelect={handleSelect}
      loading={loading}
      textField={textField}
      emptyState={
        <Box padding="4">
          <Text as="p" color="subdued" alignment="center">
            {emptyMessage}
          </Text>
        </Box>
      }
    />
  );
};

// ---------------------------------------------------------------------------
// Chips de género (Caballero / Dama) tipo toggle: una, las dos o ninguna.
// `caballero` / `dama` son booleanos controlados por el padre.
// ---------------------------------------------------------------------------
export const LeadGenderChips = ({ caballero, dama, onToggleCaballero, onToggleDama, label = 'Género de interés' }) => (
  <VerticalStack gap="1">
    <Text as="span" variant="bodyMd">
      {label}
    </Text>
    <ButtonGroup>
      <Button pressed={caballero} onClick={onToggleCaballero}>
        Caballero
      </Button>
      <Button pressed={dama} onClick={onToggleDama}>
        Dama
      </Button>
    </ButtonGroup>
  </VerticalStack>
);
